import { useEffect, useMemo, useState } from 'react';
import { Receipt, Layers } from 'lucide-react';
import apiClient from '../api/client';
import SandboxAdmin from './SandboxAdmin';

interface BillingSummary {
  tenant_id: string;
  start: string;
  end: string;
  total_cost: number;
  currency: string;
  total_duration_seconds: number;
  by_component: {
    cpu: number;
    memory: number;
    storage: number;
    container: number;
  };
  by_record_type: Record<string, number>;
  daily_breakdown: Array<{
    date: string;
    cost: number;
    duration_seconds: number;
  }>;
}

interface BillingRecord {
  id: string;
  container_id: string | null;
  runtime_id: string | null;
  record_type: string;
  period_start: string;
  period_end: string;
  duration_seconds: number;
  cpu_avg_percent: number;
  memory_avg_mb: number;
  disk_usage_mb: number;
  cost: number;
  currency: string;
}

type RangePreset = 'week' | 'month' | 'custom';

function toISODateLocal(d: Date) {
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

function startOfWeek(d: Date) {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return monday;
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}

export default function Billing() {
  const [preset, setPreset] = useState<RangePreset>('month');
  const [startDate, setStartDate] = useState<string>(toISODateLocal(startOfMonth(new Date())));
  const [endDate, setEndDate] = useState<string>(toISODateLocal(new Date()));
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [records, setRecords] = useState<BillingRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 顶部区块切换：账单（默认）/ 资源配额（嵌入 SandboxAdmin 页内容）
  const [tab, setTab] = useState<'billing' | 'sandbox'>('billing');

  // preset 切换时同步日期范围：渲染期调整（官方推荐模式），替代 effect 中同步 setState
  const [prevPreset, setPrevPreset] = useState(preset);
  if (prevPreset !== preset) {
    setPrevPreset(preset);
    const now = new Date();
    if (preset === 'week') {
      setStartDate(toISODateLocal(startOfWeek(now)));
      setEndDate(toISODateLocal(now));
    } else if (preset === 'month') {
      setStartDate(toISODateLocal(startOfMonth(now)));
      setEndDate(toISODateLocal(now));
    }
  }

  useEffect(() => {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    const params = {
      start: start.toISOString(),
      end: end.toISOString(),
    };
    // 通过 promise 链发起加载，setState 均在异步回调中执行
    Promise.resolve()
      .then(() => {
        setLoading(true);
        setError(null);
        return Promise.all([
          apiClient.get('/billing/summary', { params }),
          apiClient.get('/billing/records', { params }),
        ]);
      })
      .then(([summaryRes, recordsRes]) => {
        setSummary(summaryRes.data);
        setRecords(recordsRes.data.items || []);
      })
      .catch((e) => {
        const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
        const detail = err.response?.data?.detail;
        const message =
          typeof detail === 'string' && detail
            ? detail
            : typeof err.message === 'string'
              ? err.message
              : '';
        setError(message || '加载账单失败');
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  const componentData = useMemo(() => {
    if (!summary) return [];
    return [
      { label: 'CPU', value: summary.by_component.cpu, color: '#3b82f6' },
      { label: '内存', value: summary.by_component.memory, color: '#10b981' },
      { label: '存储', value: summary.by_component.storage, color: '#f59e0b' },
      { label: '容器基础费', value: summary.by_component.container, color: '#8b5cf6' },
    ];
  }, [summary]);

  const maxDailyCost = useMemo(() => {
    if (!summary?.daily_breakdown.length) return 0;
    return Math.max(...summary.daily_breakdown.map((d) => d.cost));
  }, [summary]);

  return (
    <div style={{ padding: '24px' }}>
      <div className="segmented-control" style={{ marginBottom: 20 }}>
        <button
          type="button"
          className={`segment ${tab === 'billing' ? 'active' : ''}`}
          onClick={() => setTab('billing')}
        >
          <Receipt size={14} className="inline mr-1" /> 账单
        </button>
        <button
          type="button"
          className={`segment ${tab === 'sandbox' ? 'active' : ''}`}
          onClick={() => setTab('sandbox')}
        >
          <Layers size={14} className="inline mr-1" /> 资源配额
        </button>
      </div>

      {tab === 'billing' ? (
        <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>账单中心</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="segmented-control">
            {(['week', 'month', 'custom'] as RangePreset[]).map((p) => (
              <button
                key={p}
                className={`segment ${preset === p ? 'active' : ''}`}
                onClick={() => setPreset(p)}
              >
                {p === 'week' ? '本周' : p === 'month' ? '本月' : '自定义'}
              </button>
            ))}
          </div>
          {preset === 'custom' && (
            <>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span>至</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </>
          )}
        </div>
      </div>

      {error && <div className="toast-error" style={{ marginBottom: 16 }}>{error}</div>}

      {summary && (
        <div className="billing-grid" style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', marginBottom: 24 }}>
          <div className="card stat-card">
            <div className="stat-label">总费用</div>
            <div className="stat-value">
              {summary.total_cost.toFixed(4)} <span className="unit">{summary.currency}</span>
            </div>
          </div>
          <div className="card stat-card">
            <div className="stat-label">计费时长</div>
            <div className="stat-value">
              {(summary.total_duration_seconds / 3600).toFixed(2)} <span className="unit">小时</span>
            </div>
          </div>
          <div className="card stat-card">
            <div className="stat-label">记录数</div>
            <div className="stat-value">{records.length}</div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', marginBottom: 24 }}>
        <div className="card">
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16 }}>费用趋势（按天）</h3>
          {summary && summary.daily_breakdown.length > 0 ? (
            <svg width="100%" height={200} viewBox={`0 0 ${Math.max(summary.daily_breakdown.length * 40, 360)} 200`}>
              {/* Axes */}
              <line x1={30} y1={10} x2={30} y2={170} stroke="var(--border-default)" />
              <line x1={30} y1={170} x2={Math.max(summary.daily_breakdown.length * 40, 360) - 20} y2={170} stroke="var(--border-default)" />
              {summary.daily_breakdown.map((point, i) => {
                const x = 40 + i * 40;
                const h = maxDailyCost > 0 ? (point.cost / maxDailyCost) * 140 : 0;
                return (
                  <g key={point.date}>
                    <rect
                      x={x - 12}
                      y={170 - h}
                      width={24}
                      height={h}
                      fill="var(--color-primary)"
                      rx={4}
                    />
                    <text
                      x={x}
                      y={188}
                      textAnchor="middle"
                      fontSize={10}
                      fill="var(--text-secondary)"
                    >
                      {point.date.slice(5)}
                    </text>
                  </g>
                );
              })}
            </svg>
          ) : (
            <div className="empty-state">{loading ? '加载中...' : '暂无账单数据'}</div>
          )}
        </div>

        <div className="card">
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16 }}>费用构成</h3>
          {componentData.length > 0 ? (
            <div>
              {componentData.map((item) => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: item.color,
                      marginRight: 10,
                    }}
                  />
                  <div style={{ flex: 1, fontSize: 14 }}>{item.label}</div>
                  <div style={{ fontWeight: 500 }}>{item.value.toFixed(4)} {summary?.currency}</div>
                </div>
              ))}
              <div style={{ marginTop: 12, height: 12, borderRadius: 6, overflow: 'hidden', display: 'flex' }}>
                {componentData.map((item) => {
                  const total = componentData.reduce((s, c) => s + c.value, 0) || 1;
                  const pct = (item.value / total) * 100;
                  return (
                    <div
                      key={item.label}
                      style={{
                        width: `${pct}%`,
                        background: item.color,
                        height: '100%',
                      }}
                      title={`${item.label}: ${pct.toFixed(1)}%`}
                    />
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="empty-state">{loading ? '加载中...' : '暂无账单数据'}</div>
          )}
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16 }}>计费明细</h3>
        {records.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>类型</th>
                <th>周期</th>
                <th>时长(秒)</th>
                <th>CPU(%)</th>
                <th>内存(MB)</th>
                <th>磁盘(MB)</th>
                <th>费用</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id}>
                  <td>{r.record_type}</td>
                  <td>
                    {new Date(r.period_start).toLocaleString()} ~ {new Date(r.period_end).toLocaleString()}
                  </td>
                  <td>{r.duration_seconds}</td>
                  <td>{r.cpu_avg_percent.toFixed(2)}</td>
                  <td>{r.memory_avg_mb.toFixed(2)}</td>
                  <td>{r.disk_usage_mb.toFixed(2)}</td>
                  <td>
                    {r.cost.toFixed(4)} {r.currency}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">{loading ? '加载中...' : '暂无计费明细'}</div>
        )}
      </div>
        </>
      ) : (
        <SandboxAdmin />
      )}
    </div>
  );
}
